import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocationsIcon } from 'assets/images/icons';
import { Typography } from '@components/Typography';
import ResponseItem from '@components/ResponseItem';
import IntegrationChip from '@components/ResponseItem/IntegrationChip';
import { InfoChip } from '@components/ResponseItem/styles';
import StatusChip, { STATUS_PALETTE } from '@components/StatusChip';
import { reviewsService } from '@services';
import { alertActions, reviewActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { formatDateOrEmpty } from '@helpers/dates';
import { getSplittedName } from '@helpers/userName';
import { useTranslation } from '@hooks';
import { ProfileName } from '@constants/integrations';
import Reply from './Reply';
import AssignContact from './AssignContact';
import MediaGallery from '@components/MediaGallery';
import { ThemeChip, AspectHighlight, KeywordsContainer, FooterWrapper, ActionsMenu } from './styles';

// INFO: 1 - positive, 0 - neutral, 2 - negative
const SENTIMENT_PRIORITY = { 2: 3, 1: 2, 0: 1 };

const getHighlightedSegments = (text, aspects) => {
    if (!text || !aspects?.length) return null;

    const lowerText = text.toLowerCase();
    const ranges = [];
    for (const aspect of aspects) {
        const candidates = [aspect.exactKeyword, aspect.context, aspect.aspect].filter(Boolean);
        if (!candidates.length) continue;

        let ctxStart = 0;
        if (aspect.context) {
            const ctxIdx = lowerText.indexOf(aspect.context.toLowerCase());
            if (ctxIdx !== -1) ctxStart = ctxIdx;
        }

        for (const candidate of candidates) {
            const searchStart = candidate === aspect.exactKeyword ? ctxStart : 0;
            const idx = lowerText.indexOf(candidate.toLowerCase(), searchStart);
            if (idx === -1) continue;

            ranges.push({ start: idx, end: idx + candidate.length, sentiment: aspect.sentiment });
            break;
        }
    }

    if (!ranges.length) return null;

    ranges.sort((a, b) => a.start - b.start);

    // Merge overlapping ranges; for conflicts prefer negative > positive > neutral
    const merged = [];
    for (const range of ranges) {
        if (!merged.length || range.start > merged[merged.length - 1].end) {
            merged.push({ ...range });
        } else {
            const last = merged[merged.length - 1];
            last.end = Math.max(last.end, range.end);
            if (SENTIMENT_PRIORITY[range.sentiment] > SENTIMENT_PRIORITY[last.sentiment]) {
                last.sentiment = range.sentiment;
            }
        }
    }

    const segments = [];
    let pos = 0;
    for (const range of merged) {
        if (range.start > pos) segments.push({ text: text.slice(pos, range.start), sentiment: null });
        segments.push({ text: text.slice(range.start, range.end), sentiment: range.sentiment });
        pos = range.end;
    }
    if (pos < text.length) segments.push({ text: text.slice(pos), sentiment: null });

    return segments;
};

const INTERFACE_LANG_TO_ISO = {
    GB: 'en',
    UA: 'uk',
    PT: 'pt',
    ES: 'es',
    BR: 'pt',
    MX: 'es',
};

const GOOGLE_TRANSLATE_API_KEY = 'AIzaSyANGkyyh_ZoH8GlNIGKUQF6a9vPIf--kMo';

const FeedbackBody = ({ item, language, isMobile }) => {
    const { t } = useTranslation();
    const [translatedText, setTranslatedText] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);

    const interfaceLangISO = INTERFACE_LANG_TO_ISO[language];
    const showTranslateButton = !!(item?.text && item?.language && item.language !== interfaceLangISO);
    const displayText = translatedText || item?.text;
    const segments = translatedText ? null : getHighlightedSegments(item?.text, item?.aspects);

    const handleTranslate = async () => {
        if (translatedText) {
            setTranslatedText(null);
            return;
        }
        setIsTranslating(true);
        try {
            const response = await fetch(
                `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_TRANSLATE_API_KEY}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ q: item.text, target: interfaceLangISO, format: 'text' }),
                },
            );
            const data = await response.json();
            setTranslatedText(data.data.translations[0].translatedText);
        } catch (_) {
            // silently fail
        } finally {
            setIsTranslating(false);
        }
    };

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: 'pre-line',
                        ...(!item?.text && { color: 'var(--text-tertiary)' }),
                    }}
                >
                    {segments
                        ? segments.map((seg, i) =>
                              seg.sentiment !== null ? (
                                  <AspectHighlight key={i} sentiment={seg.sentiment}>
                                      {seg.text}
                                  </AspectHighlight>
                              ) : (
                                  seg.text
                              ),
                          )
                        : displayText || t('ReviewsManage.onlyRating')}
                </Typography>
                {showTranslateButton && (
                    <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                cursor: isTranslating ? 'default' : 'pointer',
                                color: 'var(--primary-main)',
                                textDecoration: 'underline',
                                opacity: isTranslating ? 0.5 : 1,
                            }}
                            onClick={!isTranslating ? handleTranslate : undefined}
                        >
                            {translatedText ? t('ReviewsManage.showOriginal') : t('ReviewsManage.translate')}
                        </Typography>
                        {translatedText && (
                            <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                                {t('ReviewsManage.translatedByGoogle')}
                            </Typography>
                        )}
                    </div>
                )}
            </div>
            <MediaGallery media={item?.media} />
            <Reply language={language} review={item} isMobile={isMobile} />
        </>
    );
};

const REVIEW_STATUS_TONE = {
    updated: 'warning',
    deleted: 'danger',
};

const getReviewStatus = item => {
    if (item?.isDeleted) return 'deleted';
    const created = item?.creationDate ? new Date(item.creationDate).getTime() : null;
    const updated = item?.updatedAt ? new Date(item.updatedAt).getTime() : null;
    if (created != null && updated != null && updated > created) return 'updated';
    return null;
};

const LocationChip = ({ companyName, onClick }) => {
    if (!companyName) return null;
    return (
        <InfoChip onClick={onClick}>
            <LocationsIcon />
            <Typography variant="caption" sx={{ color: 'var(--text-primary)' }}>
                {companyName}
            </Typography>
        </InfoChip>
    );
};

const CurationMenu = ({ item }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [anchor, setAnchor] = useState({ id: null, el: null });

    const closeMenu = () => setAnchor({ id: null, el: null });

    const run = async (request, successKey) => {
        closeMenu();
        try {
            const updatedReview = await request(item.reviewId);
            dispatch(reviewActions.updateReview(updatedReview));
            if (successKey) dispatch(alertActions.success(t(successKey)));
        } catch (error) {
            handleApiError(error);
        }
    };

    const menu = [
        {
            id: 'feature',
            title: t(item?.isFeatured ? 'ReviewsManage.removeFromFeatured' : 'ReviewsManage.markAsFeatured'),
            onClick: () => run(item?.isFeatured ? reviewsService.unfeature : reviewsService.feature),
        },
        item?.isDeleted
            ? {
                  id: 'unmark-deleted',
                  title: t('ReviewsManage.unmarkAsDeleted'),
                  onClick: () => run(reviewsService.unmarkDeleted, 'ReviewsManage.unmarkedAsDeleted'),
              }
            : {
                  id: 'mark-deleted',
                  title: t('ReviewsManage.markAsDeleted'),
                  onClick: () => run(reviewsService.markDeleted, 'ReviewsManage.markedAsDeleted'),
              },
    ];

    return (
        <ActionsMenu
            id={item?.reviewId}
            menu={menu}
            anchor={anchor}
            handleOpenEl={(e, id) => setAnchor({ id, el: e.currentTarget })}
            handleCloseEl={closeMenu}
        />
    );
};

const ReviewItem = ({ item, index, language, isMobile, companies, onClickContactDetails, onFilterByLocation }) => {
    const { t } = useTranslation();

    const date = formatDateOrEmpty(item?.updatedAt, 'd MMM yyyy', language);

    const companyName = companies?.find(company => company.companyId === item?.locationId)?.internalName;
    const { firstName, lastName } = getSplittedName(item?.userName);
    const isIFood = item?.integrationName === ProfileName.iFood;
    const reviewStatus = getReviewStatus(item);
    const isFeatured = !!item?.isFeatured;

    return (
        <ResponseItem
            key={item?.id || `feedback-${index}`}
            index={index}
            firstName={firstName}
            lastName={lastName}
            userNameTitle={isIFood ? `(${t(`common.${item?.isPrivate ? 'private' : 'public'}`)})` : null}
            viewed={item?.viewed}
            rating={item?.rating}
            date={date}
            userProfileImg={item?.userPhotoUrl}
            nameChip={
                reviewStatus || isFeatured ? (
                    <>
                        {reviewStatus && (
                            <StatusChip
                                bg={STATUS_PALETTE[REVIEW_STATUS_TONE[reviewStatus]].bg}
                                fg={STATUS_PALETTE[REVIEW_STATUS_TONE[reviewStatus]].fg}
                                label={t(`ReviewsManage.reviewStatus.${reviewStatus}`)}
                            />
                        )}
                        {isFeatured && (
                            <StatusChip
                                bg={STATUS_PALETTE.success.bg}
                                fg={STATUS_PALETTE.success.fg}
                                label={t('ReviewsManage.reviewStatus.featured')}
                            />
                        )}
                    </>
                ) : null
            }
            chips={
                <>
                    <IntegrationChip profileName={item?.integrationName} link={item?.link} />
                    <LocationChip companyName={companyName} onClick={() => onFilterByLocation(item?.locationId)} />
                </>
            }
            actions={<CurationMenu item={item} />}
            contactDetailsLink={
                <AssignContact
                    contactId={item?.contactId}
                    contactName={item?.contactName}
                    reviewId={item?.reviewId}
                    contactPhoneNumber={item?.contactPhoneNumber}
                    contactEmail={item?.contactEmail}
                    contactExternalId={item?.contactExternalId}
                    onClickContactDetails={onClickContactDetails}
                />
            }
            body={<FeedbackBody item={item} language={language} isMobile={isMobile} />}
            footer={
                !!item?.themes?.length && (
                    <FooterWrapper>
                        <KeywordsContainer>
                            {item?.themes?.map((theme, index) => (
                                <ThemeChip key={index} variant="caption">
                                    {t(`themes.${theme.theme}`, theme.theme)}
                                </ThemeChip>
                            ))}
                        </KeywordsContainer>
                    </FooterWrapper>
                )
            }
            footerStyles={{ justifyContent: 'flex-start' }}
        />
    );
};

ReviewItem.displayName = 'ReviewItem';

export default ReviewItem;
